import React from 'react';
import { t } from 'i18next';
import GolfIcon from '../GolfIcon';
import { PlayerResult } from './GameResult';
import { cn } from '@/lib/utils';
import { AggregatedStats } from '@/types';

interface ScoreByParStats {
  par3: {
    total: number;
    toPar: number;
    count: number;
  };
  par4: {
    total: number;
    toPar: number;
    count: number;
  };
  par5: {
    total: number;
    toPar: number;
    count: number;
  };
}

interface ScoreByParProps {
  player?: PlayerResult;
  stats?: AggregatedStats;
  className?: string;
}

const ScoreByPar: React.FC<ScoreByParProps> = ({ player, stats: providedStats, className }) => {
  // Calculate statistics for each par type from player data
  const calculateStatsFromPlayer = (): ScoreByParStats => {
    const statsObj = {
      par3: { total: 0, toPar: 0, count: 0 },
      par4: { total: 0, toPar: 0, count: 0 },
      par5: { total: 0, toPar: 0, count: 0 },
    };

    if (player && player.scores) {
      player.scores.forEach(hole => {
        if (!hole.par || !hole.score) return;

        if (hole.par === 3) {
          statsObj.par3.total += hole.score;
          statsObj.par3.toPar += hole.score - hole.par;
          statsObj.par3.count++;
        } else if (hole.par === 4) {
          statsObj.par4.total += hole.score;
          statsObj.par4.toPar += hole.score - hole.par;
          statsObj.par4.count++;
        } else if (hole.par === 5) {
          statsObj.par5.total += hole.score;
          statsObj.par5.toPar += hole.score - hole.par;
          statsObj.par5.count++;
        }
      });
    }

    return statsObj;
  };

  // Extract stats from provided stats object (from StatsPage)
  const extractStatsFromAggregated = (): ScoreByParStats => {
    if (!providedStats) return {
      par3: { total: 0, toPar: 0, count: 0 },
      par4: { total: 0, toPar: 0, count: 0 },
      par5: { total: 0, toPar: 0, count: 0 },
    };

    return {
      par3: providedStats.par3Score || { total: 0, toPar: 0, count: 0 },
      par4: providedStats.par4Score || { total: 0, toPar: 0, count: 0 },
      par5: providedStats.par5Score || { total: 0, toPar: 0, count: 0 },
    };
  };

  // Use either the calculated stats from player or the provided stats
  const parStats = providedStats ? extractStatsFromAggregated() : calculateStatsFromPlayer();

  // Calculate average for each par type
  const getAverage = (total: number, count: number): string => {
    if (count === 0) return '-';
    return (total / count).toFixed(1);
  };

  // Calculate the max average score for chart scaling
  const par3Avg = parStats.par3.count ? parStats.par3.total / parStats.par3.count : 0;
  const par4Avg = parStats.par4.count ? parStats.par4.total / parStats.par4.count : 0;
  const par5Avg = parStats.par5.count ? parStats.par5.total / parStats.par5.count : 0;

  return (
    <div className={cn("bg-card rounded-2xl p-4", className)}>
      <div className="flex items-center gap-2 mb-6">
        <GolfIcon name="golf-hole" className="size-5" />
        <span className="text-sm font-semibold">{t('score_by_par')}</span>
      </div>

      <div className='relative'>
        {par3Avg === 0 && par4Avg === 0 && par5Avg === 0 ? (
          <div className="absolute inset-0 items-center justify-center bg-card/80 z-50 text-center p-4 flex flex-col">
            <p className="text-muted-foreground text-sm">{t('no_data')}</p>
            <p className="text-muted-foreground text-sm">{t('continue_play_see_stats')}</p>
          </div>) : ''}
        <div className="flex justify-between px-8 items-end h-40 relative">
          {/* Reference lines from 1 to 6 shots */}
          {[1, 2, 3, 4, 5, 6].map((shots) => (
            <div
              key={shots}
              className="absolute left-4 right-0 border-b border-dashed border-gray-200 dark:border-gray-700"
              style={{ bottom: `${(shots / 6) * 100}%` }}
            >
              <div className="absolute -left-5 top-1/2 -translate-y-1/2">
                <span className="text-xs text-muted-foreground">{shots}</span>
              </div>
            </div>
          ))}

          {/* Par 3 Bar */}
          <div className="relative h-full flex flex-col justify-end items-center basis-1/4 opacity-60">
            <div
              className="w-12 bg-purple-500 bg-gradient-to-t from-background dark:from-purple-800 to-purple-500 rounded-t-sm"
              style={{ height: `${(par3Avg / 6) * 100}%` }}
            ></div>
          </div>

          {/* Par 4 */}
          <div className="relative h-full flex flex-col justify-end items-center basis-1/4 opacity-60">
            <div
              className="w-12 bg-blue-500 bg-gradient-to-t from-background dark:from-blue-800 to-blue-500 rounded-t-sm"
              style={{ height: `${(par4Avg / 6) * 100}%` }}
            ></div>
          </div>

          {/* Par 5 */}
          <div className="relative h-full flex flex-col justify-end items-center basis-1/4 opacity-60">
            <div
              className="w-12 bg-emerald-500 bg-gradient-to-t from-background dark:from-emerald-800 to-emerald-500 rounded-t-sm"
              style={{ height: `${(par5Avg / 6) * 100}%` }}
            ></div>
          </div>
        </div>

        <div className="flex justify-between px-8 pt-2">
          <div className="text-center basis-1/4">
            <div className="text-xs font-medium">PAR 3</div>
            <div className="text-xs text-gray-500">{getAverage(parStats.par3.total, parStats.par3.count)}</div>
          </div>
          <div className="text-center basis-1/4">
            <div className="text-xs font-medium">PAR 4</div>
            <div className="text-xs text-gray-500">{getAverage(parStats.par4.total, parStats.par4.count)}</div>
          </div>
          <div className="text-center basis-1/4">
            <div className="text-xs font-medium">PAR 5</div>
            <div className="text-xs text-gray-500">{getAverage(parStats.par5.total, parStats.par5.count)}</div>
          </div>
        </div>
      </div>
    </div >
  );
};

export default ScoreByPar;
